import { Component, ElementRef, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { forkJoin, map, of, switchMap } from 'rxjs';

import {
  faBan,
  faCheckCircle,
  faCircleExclamation,
  faDollarSign,
  faPrint,
  faRotateLeft,
  faSpinner,
  faUserGraduate,
  faUserPen,
  faUserPlus,
  faUserXmark,
  faCircleXmark,
  faUserGroup,
  faUsersBetweenLines,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

import { StudentsService } from '../../core/services/students.service';
import { UtilityService } from '../../core/services/utility.service';

import { SchoolClass } from '../../core/models/school-class.model';
import { Student } from '../../core/models/student.model';
import { LoginService } from '../../core/services/login.service';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { ManagementUser } from '../../core/models/management-user.model';
import { DialogService } from 'src/app/core/services/dialog.service';
import { PrintService } from 'src/app/core/services/print.service';

import introJs from 'intro.js';
import { studentsListIntroSteps } from './students-list.intro-steps';

@Component({
    selector: 'wf-students-list',
    templateUrl: './students-list.component.html',
    styleUrls: ['./students-list.component.css'],
    standalone: false
})
export class StudentsListComponent implements OnInit, AfterViewInit {
  @ViewChild('printContent') printContent!: ElementRef<HTMLElement>;
  managementUser: ManagementUser | null = null;
  isCleverTeacher: boolean = false;
  isClasslinkTeacher: boolean = false;
  isParentUser: boolean = false;
  userRole: string = '';
  isDeleting: boolean = false;
  canPurchase: boolean = false;
  showCancelDialog: boolean = false;
  showPurchaseDialog: boolean = false;
  showSectionAddMessage: boolean = false;
  messageTitle: string = '';
  message: string = '';
  messageStatus: string = '';
  students: Student[] = [];
  displayedStudents: Student[] =[];
  mainFilteredStudents: Student[] = []; // stores the result of the main filter (grades, classes, etc.)
  grades: string[] = [];
  classes: SchoolClass[] = [];
  selectedStudents: Set<number> = new Set<number>();
  studentsSelected: boolean = false;
  sectionAddMessage: any = {};
  currentFilters: any = null;
  hideSharedStudents: boolean = false;
  showCoTeacherFilter: boolean = false;

  // Icons
  printIcon = faPrint;
  studentIcon = faUserGraduate;
  addStudentIcon = faUserPlus;
  deleteStudentIcon = faUserXmark;
  editStudentIcon = faUserPen;
  loadingIcon = faSpinner;
  successIcon = faCheckCircle;
  errorIcon = faCircleExclamation;
  purchaseIcon = faDollarSign;
  cancelIcon = faBan;
  returnIcon = faRotateLeft;
  closeIcon = faCircleXmark;
  enrollIcon = faUserGroup;
  coTeacherIcon = faUsersBetweenLines;
  helpIcon = faQuestionCircle;
  introTour: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private studentService: StudentsService,
    private utilityService: UtilityService,
    private loginService: LoginService,
    private sessionService: SessionStorageService,
    private dialogService: DialogService,
    private printService: PrintService
  ) { }

  ngOnInit(): void {
    this.managementUser = this.sessionService.getUserData();
    if (this.managementUser) {
      this.isParentUser = this.managementUser.isParentUser();
      this.userRole = this.managementUser.userRole;
      this.showCoTeacherFilter = this.managementUser.isTeacherUser();
    }

    this.students = this.route.snapshot.data['resolveData'].students;
    this.mainFilteredStudents = this.students;
    this.grades = this.utilityService.sortGrades(this.route.snapshot.data['resolveData'].grades);
    this.classes = this.utilityService.sortClasses(this.route.snapshot.data['resolveData'].classes);
    this.canPurchase = this.isParentUser && this.route.snapshot.data['resolveData'].licenseCount === 0;
    this.isCleverTeacher = this.sessionService.getSSOInfo()?.provider === 'clever';
    this.isClasslinkTeacher = this.sessionService.getSSOInfo()?.provider === 'classlink';
    this.sectionAddMessage = this.dialogService.getSectionAddMessage();
    if (this.sectionAddMessage.showMessage) {
      this.showSectionAddMessage = true;
    }

    if (this.managementUser && this.managementUser.isTeacherUser()) {
      this.checkForSharedStudents();
    }

    this.updateDisplayedStudents();
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: studentsListIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }
      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(studentsListIntroSteps);
    this.introTour.setOptions({
      steps: filteredSteps
    });
    this.introTour.start();
  }

  studentToggled(e: any) {
    if (e.target.checked) {
      this.selectedStudents.add(e.target.value);
    } else {
      this.selectedStudents.delete(e.target.value);
    }
    this.studentsSelected = this.selectedStudents.size > 0;
  }

  // hide the shared students toggle for would-be valid users that
  // don't have students shared with them.
  checkForSharedStudents() {
    if (this.showCoTeacherFilter) {
      const hasAnySharedStudents = this.students.some(student => student.fromCoTeacherAccess);
      this.showCoTeacherFilter = hasAnySharedStudents;
    }
  }

  filterStudents(filterOpts: any) {
    this.currentFilters = filterOpts;
    this.mainFilteredStudents = this.studentService.filterStudents(this.students, filterOpts);
    this.updateDisplayedStudents();
    this.selectedStudents = new Set<number>();
  }

  onCoTeacherFilterChange() {
    this.updateDisplayedStudents();
  }

  private updateDisplayedStudents() {
    if (!this.hideSharedStudents || !this.showCoTeacherFilter) {
      this.displayedStudents = this.mainFilteredStudents;
    } else {
      this.displayedStudents = this.mainFilteredStudents.filter(student => !student.fromCoTeacherAccess);
    }
  }

  archiveStudents() {
    this.isDeleting = true;
    this.studentService.archiveStudents(Array.from(this.selectedStudents)).pipe(
      map((deleteStatuses) => {
        let failedCount = 0;
        let successCount = 0;

        deleteStatuses.forEach((status: number) => {
          if (status === 200) successCount++;
          else failedCount++;
        });
        return {
          failedCount: failedCount,
          successCount: successCount,
        };
      }),
      switchMap((statusCount) => {
        return forkJoin({
          statusCount: of(statusCount),
          students: this.studentService.getStudents()
        });
      })
    ).subscribe({
      next: (results: any) => {
        let failedCount: number = results['statusCount']['failedCount'];
        let successCount: number = results['statusCount']['successCount'];
        this.students = results['students'];
        this.filterStudents(this.currentFilters || {}); // Re-apply filters
        this.selectedStudents = new Set<number>();
        this.isDeleting = false;

        this.messageTitle = 'Student Deletion';
        if (failedCount > 0) {
          this.messageStatus = 'error';
          this.message = `There was a problem deleting ${failedCount} ${failedCount > 1 ? 'students' : 'student'}`;
        } else {
          this.messageStatus = 'success';
          this.message = `${successCount} student${successCount > 1 ? 's were' : ' was'} successfully deleted`;
        }
      }
    });
  }

  cancelStudentSubscriptions() {
    this.studentService.cancelStudentSubscriptions(Array.from(this.selectedStudents)).pipe(
      map((cancelStatuses) => {
        return cancelStatuses.filter((status: number) => status !== 200).length;
      }),
      switchMap((failedCount: any) => {
        this.messageTitle = 'Subscription Cancellation';
        if (failedCount > 0) {
          this.messageStatus = 'error';
          this.message = `There was a problem cancelling your ${failedCount} ${failedCount > 1 ? 'subscriptions' : 'subscription'}`;
        } else {
          this.messageStatus = 'success';
          this.message = `${this.selectedStudents.size} subscription${this.selectedStudents.size > 1 ? 's were' : ' was'} successfully cancelled`;
        }
        return this.studentService.isParentEnabled(this.sessionService.getUserData()?.userID);
      }),
      switchMap((parentEnabled: boolean) => {
        if (!parentEnabled) {
          this.loginService.logout();
          return of([]);
        } else {
          return this.studentService.getStudents();
        }
      })
    ).subscribe({
      next: (students) => {
        if (students.length > 0) {
          this.students = students;
          this.filterStudents(this.currentFilters || {}); // Re-apply filters
        }
        this.selectedStudents = new Set<number>();
        this.showCancelDialog = false;
      }
    });
  }

  addStudent() {
    this.router.navigateByUrl('student/add');
  }

  editStudent(studentId: number) {
    this.router.navigateByUrl(`student/edit/${studentId}`);
  }

  goToAddCleverSections() {
    this.router.navigateByUrl('clever/roster');
  }

  goToAddClassLinkSections() {
    this.router.navigateByUrl('classlink/roster');
  }

  closeSectionAddNotification() {
    this.showSectionAddMessage = false;
    this.dialogService.clearSectionAddMessage();
  }

  printStudents() {
    const userData = this.sessionService.getUserData();
    const teacher = `${userData?.firstName} ${userData?.lastName}`;
    
    let filterText = '';
    if (this.currentFilters) {
      const filterParts = [];
      
      if (this.currentFilters.grade && this.currentFilters.grade !== 'all') {
        let gradeDisplay = '';
        if (this.currentFilters.grade === 'four_below_all') {
          gradeDisplay = 'All 4th & Below';
        } else if (this.currentFilters.grade === 'five_up_all') {
          gradeDisplay = 'All 5th & Up';
        } else {
          gradeDisplay = `Grade ${this.currentFilters.grade}`;
        }
        filterParts.push(`Grade: ${gradeDisplay}`);
      }
      
      if (this.currentFilters.class && this.currentFilters.class !== 0) {
        const selectedClass = this.classes.find(c => c.schoolClassID === this.currentFilters.class);
        if (selectedClass && selectedClass.name !== 'All') {
          filterParts.push(`Class: ${selectedClass.name}`);
        }
      }
      
      if (filterParts.length > 0) {
        filterText = filterParts.join(' | ');
      }
    }
    
    this.printService.openPrintWindow(
      this.printContent.nativeElement, 
      userData?.school || '', 
      teacher,
      filterText
    );
  }
}